import React from 'react';
import Modal from '@hero/ui-kit/components/Modal';
import H from '@hero/ui-kit/typography/H';
import P from '@hero/ui-kit/typography/P';
import Button from '@hero/ui-kit/inputs/Button';
import Section from '@hero/ui-kit/layout/Section';
import DatePicker from '@hero/ui-kit/components/DatePicker';
import { format, setHours, setMinutes, isToday } from 'date-fns';
import useUpdateLeadApprovalStatus from '../../api/useUpdateLeadApprovalStatus';
import { ApprovalStatus } from '../../types';
import * as Style from './style.module.scss';

// HWM-22859: manual medical-check scheduling for providers WITHOUT a DrChrono
// integration. Restores the pre-redesign date/time + note modal that was
// dropped in favour of the inline NewActivityForm path — a path that silently
// scheduled with no date when the backend stopped advertising the date/time
// requirement.
//
// Uses the same UI-kit `DatePicker` as NewActivityForm (date mode + time-only
// mode) so the picker matches the rest of the portal. DrChrono orgs keep the
// slot-picker `ScheduleMedicalCheckModal`. Posts the shared
// update-approval-status endpoint: `approval_status=MEDICAL_CHECK` +
// `note_date_time` (stored on `PatientLead.status_change_note_date_time`) +
// an optional `note`. The backend rejects a date-less MEDICAL_CHECK, so a
// date/time is required.
interface ManualMedicalCheckModalProps {
    leadId: string;
    patientName?: string;
    isReschedule?: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

const ManualMedicalCheckModal: React.FC<ManualMedicalCheckModalProps> = ({
    leadId,
    patientName,
    isReschedule = false,
    onClose,
    onSuccess
}) => {
    const [date, setDate] = React.useState<Date | undefined>();
    const [time, setTime] = React.useState<Date | undefined>();
    const [note, setNote] = React.useState('');
    const now = new Date();

    const { mutate: schedule, isPending } = useUpdateLeadApprovalStatus(() => {
        onSuccess && onSuccess();
        onClose();
    });

    const handleSubmit = () => {
        if (!date || !time) return;
        // Combine the chosen day + time-of-day into one local instant.
        const combined = new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            time.getHours(),
            time.getMinutes(),
            0
        );
        // The backend stores `note_date_time` as UTC (USE_TZ) and the API
        // returns it with a UTC offset, which the portal renders back in local
        // time. So send the UTC wall-clock as a naive "yyyy-MM-dd HH:mm:ss"
        // string — mirrors NewActivityForm — otherwise the displayed time is
        // shifted by the local UTC offset (HWM-22859).
        const utcWallClock = new Date(
            combined.getUTCFullYear(),
            combined.getUTCMonth(),
            combined.getUTCDate(),
            combined.getUTCHours(),
            combined.getUTCMinutes(),
            combined.getUTCSeconds()
        );
        schedule({
            payload: {
                lead_id: leadId,
                approval_status: 'MEDICAL_CHECK' as ApprovalStatus,
                note_date_time: format(utcWallClock, 'yyyy-MM-dd HH:mm:ss'),
                ...(note.trim() && { note: note.trim() })
            }
        });
    };

    const canSubmit = !!date && !!time;

    return (
        <Modal isCancelable onClose={onClose} className={Style.modalSmall}>
            <div className={Style.modalBody}>
                <H role="h4" centered noDefaultMargin>
                    {`${isReschedule ? 'Reschedule medical check' : 'Schedule medical check'}${
                        patientName ? ` – ${patientName}` : ''
                    }`}
                </H>

                <div className={Style.fieldGroup}>
                    <P noDefaultMargin className={Style.uppercaseLabel}>
                        Date
                    </P>
                    <div className={Style.pillPicker}>
                        <DatePicker
                            selected={date}
                            minDate={now}
                            onChange={(value: Date | null) => setDate(value ?? undefined)}
                        />
                    </div>
                </div>

                <div className={Style.fieldGroup}>
                    <P noDefaultMargin className={Style.uppercaseLabel}>
                        Time
                    </P>
                    <div className={Style.pillPicker}>
                        <DatePicker
                            disabled={!date}
                            selected={time}
                            onChange={(value: Date | null) => setTime(value ?? undefined)}
                            showTimeSelect
                            showTimeSelectOnly
                            timeIntervals={15}
                            timeCaption="Time"
                            dateFormat="h:mm aa"
                            minTime={date && isToday(date) ? now : undefined}
                            maxTime={
                                date && isToday(date)
                                    ? setHours(setMinutes(now, 59), 23)
                                    : undefined
                            }
                        />
                    </div>
                </div>

                <div className={Style.fieldGroup}>
                    <P noDefaultMargin className={Style.uppercaseLabel}>
                        Note (optional)
                    </P>
                    <textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        className={Style.textarea}
                        placeholder="Add a note about the scheduled medical check…"
                    />
                </div>

                <Section noDefaultPadding className={Style.actionsCentered}>
                    <Button variant="outlineSecondary" onClick={onClose} disabled={isPending}>
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} disabled={!canSubmit || isPending}>
                        {isPending ? 'Scheduling…' : 'Schedule'}
                    </Button>
                </Section>
            </div>
        </Modal>
    );
};

export default ManualMedicalCheckModal;
